<template>
  <div
    class="form-checkbox"
    :class="[checkboxSize, { 'has-error': error }]"
    v-tooltip="{ content: tooltipContent, placement: 'top', offset: '8px' }"
  >
    <input
      class="form-checkbox__input"
      v-bind:class="{ 'is-checked': indeterminate }"
      :checked="indeterminate"
      :disabled="disabled"
      :id="checkboxId"
      :name="checkboxId"
      :aria-label="ariaLabel || null"
      type="checkbox"
      v-on:change="forceChecked(afterIndeterminate)"
      v-if="indeterminate"
    />
    <input
      class="form-checkbox__input"
      v-bind:class="{ 'is-checked': value }"
      :checked="value"
      :disabled="disabled"
      :id="checkboxId"
      :name="checkboxId"
      :aria-label="ariaLabel || null"
      type="checkbox"
      @change="checked"
      v-else
    />

    <label :for="checkboxId" class="form-checkbox__label">
      <div class="form-checkbox__check">
        <BaseIcon name="minus" v-if="indeterminate" />
        <BaseIcon name="check" v-else />
      </div>
      <slot v-if="terms" name="label">label</slot>
      <div v-else class="form-checkbox__text-container" v-bind:class="{ label: !!label }">
        <span class="label__text">{{ label }}</span>
        <span class="label__subtext">{{ sublabel }}</span>
      </div>
    </label>
    <span v-if="error" class="form-checkbox__error">{{ error }}</span>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon.vue';

export default {
  name: 'FormCheckbox',
  components: {
    BaseIcon,
  },
  $_veeValidate: {
    value() {
      return this.value;
    },
  },
  props: {
    value: Boolean,
    error: {
      type: String,
      default: null,
    },
    disabled: {
      default: () => false,
      type: Boolean,
    },
    indeterminate: {
      default: () => false,
      type: Boolean,
    },
    // What value should the input have after an `indeterminate` checkbox is clicked
    afterIndeterminate: {
      default: () => true,
      type: Boolean,
    },
    label: {
      type: String,
      required: false,
    },
    // Accessible name for checkboxes with no visible label (e.g. table row/select-all).
    ariaLabel: {
      type: String,
      required: false,
    },
    sublabel: {
      type: String,
      required: false,
    },
    size: {
      type: String,
      default: 'normal',
    },
    onChange: Function,
    terms: {
      type: Boolean,
      default: false,
    },
    tooltipContent: {
      type: String,
      default: null,
    },
  },
  computed: {
    checkboxSize() {
      return `form-checkbox--${this.size}`;
    },
    checkboxId() {
      return this.$uuid.v4();
    },
  },
  methods: {
    checked(event) {
      this.forceChecked(event.target.checked);
    },
    forceChecked(value) {
      if (this.disabled) {
        return;
      }
      this.$emit('input', value);
      if (this.onChange) {
        this.onChange(value);
      }
    },
  },
};
</script>

<style lang="scss">
.form-checkbox {
  padding: 8px;
  margin-right: 8px;
  cursor: pointer;

  &__input {
    position: absolute;
    opacity: 0;
    z-index: -1;

    &:focus {
      + .form-checkbox__label .form-checkbox__check {
        box-shadow: $shadow-focus-outline;
      }
    }
  }

  &__check {
    align-items: center;
    border-radius: $border-radius-small;
    border: 1px solid $color-neutral;
    display: flex;
    flex: none;
    height: 24px;
    justify-content: center;
    position: relative;
    transition: background 200ms ease, border 200ms ease;
    width: 24px;

    .feather {
      color: $color-white;
      display: none;
      height: 18px;
      width: 18px;

      &:hover {
        path {
          color: $color-white;
        }
      }
    }
  }

  &__text-container {
    display: flex;
    flex-direction: column;

    &.label {
      margin-left: 16px;
    }

    .label__subtext {
      color: $color-normal-pale;
      font-weight: normal;
    }
  }

  &__label {
    align-items: center;
    display: flex;
    font-size: $font-size-small;
    cursor: pointer;
  }

  &:hover {
    .form-checkbox__check {
      border-color: $color-normal;
    }
  }

  // Style du check visuel quand disabled
  .form-checkbox__input:disabled + .form-checkbox__label .form-checkbox__check {
    background-color: $color-neutral-pale;
    border-color: $color-border;
  }

  &__error {
    bottom: 37px;
    color: $color-error;
    height: 16px;
    position: absolute;
    right: -20px;
    width: 16px;
  }

  &.has-error {
    .form-checkbox__check {
      border-color: $color-error;
    }

    .label__text {
      color: $color-error;
    }
  }
}

// État selectionné
.form-checkbox__input.is-checked {
  + .form-checkbox__label {
    font-weight: bold;

    .form-checkbox__check {
      background-color: $color-accent;
      border-color: $color-accent;

      .feather {
        display: block;
      }
    }
  }

  &:disabled {
    + .form-checkbox__label {
      .form-checkbox__check {
        background-color: $color-dark-blue;
        border-color: $color-dark-blue;
      }
    }
  }
}

// Variante small
.form-checkbox--small {
  .form-checkbox__check {
    height: 16px;
    width: 16px;

    .feather {
      height: 14px;
      width: 14px;
    }
  }
  .form-checkbox__label {
    font-size: $font-size-xsmall;
  }
}
</style>

<docs>
  Usage :
  <FormCheckbox v-model="parentBoolean" :label="myLabel" size="normal : small"/>

  TODO, il faudrait voir comment on fait la validation pour required.
  Prop? Est-ce qu'on support un groupe avec au moins une option requise?
</docs>
